if (!customElements.get('product-form')) {
  customElements.define(
    'product-form',
    class ProductForm extends HTMLElement {
      constructor() {
        super();

        this.form = this.querySelector('form');
        this.variantIdInput.disabled = false;
        this.form.addEventListener('submit', this.onSubmitHandler.bind(this));
        this.cart = document.querySelector('cart-notification') || document.querySelector('cart-drawer');
        this.submitButton = this.querySelector('[type="submit"]');
        this.submitButtonText = this.submitButton.querySelector('span');

        if (document.querySelector('cart-drawer')) this.submitButton.setAttribute('aria-haspopup', 'dialog');

        this.hideErrors = this.dataset.hideErrors === 'true';
      }

      onSubmitHandler(evt) {
        evt.preventDefault();
        if (this.submitButton.getAttribute('aria-disabled') === 'true') return;

        this.handleErrorMessage();

        this.submitButton.setAttribute('aria-disabled', true);
        this.submitButton.classList.add('loading');
        this.querySelector('.loading__spinner').classList.remove('hidden');

        const config = fetchConfig('javascript');
        config.headers['X-Requested-With'] = 'XMLHttpRequest';
        delete config.headers['Content-Type'];

        const formData = new FormData(this.form);
        const addOnItems = ProductForm.getAddOnItems(formData);
        ProductForm.ADD_ON_FIELDS.forEach((field) => {
          formData.delete(field.select);
          formData.delete(field.quantity);
        });

        if (this.cart) {
          formData.append(
            'sections',
            this.cart.getSectionsToRender().map((section) => section.id)
          );
          formData.append('sections_url', window.location.pathname);
          this.cart.setActiveElement(document.activeElement);
        }
        config.body = formData;

        fetch(`${routes.cart_add_url}`, config)
          .then((response) => response.json())
          .then((response) => {
            if (response.status) {
              publish(PUB_SUB_EVENTS.cartError, {
                source: 'product-form',
                productVariantId: formData.get('id'),
                errors: response.errors || response.description,
                message: response.message,
              });
              this.handleErrorMessage(response.description);

              const soldOutMessage = this.submitButton.querySelector('.sold-out-message');
              if (!soldOutMessage) return;
              this.submitButton.setAttribute('aria-disabled', true);
              this.submitButtonText.classList.add('hidden');
              soldOutMessage.classList.remove('hidden');
              this.error = true;
              return;
            } else if (!this.cart) {
              window.location = window.routes.cart_url;
              return;
            }

            if (addOnItems.length) {
              return this.addAddOnItems(addOnItems, formData).then((addOnResponse) => addOnResponse || response);
            }

            return response;
          })
          .then((response) => {
            if (!response) return;

            const startMarker = CartPerformance.createStartingMarker('add:wait-for-subscribers');
            if (!this.error)
              publish(PUB_SUB_EVENTS.cartUpdate, {
                source: 'product-form',
                productVariantId: formData.get('id'),
                cartData: response,
              }).then(() => {
                CartPerformance.measureFromMarker('add:wait-for-subscribers', startMarker);
              });
            this.error = false;
            const quickAddModal = this.closest('quick-add-modal');
            if (quickAddModal) {
              document.body.addEventListener(
                'modalClosed',
                () => {
                  setTimeout(() => {
                    CartPerformance.measure("add:paint-updated-sections", () => {
                      this.cart.renderContents(response);
                    });
                  });
                },
                { once: true }
              );
              quickAddModal.hide(true);
            } else {
              CartPerformance.measure("add:paint-updated-sections", () => {
                this.cart.renderContents(response);
              });
            }
          })
          .catch((e) => {
            console.error(e);
          })
          .finally(() => {
            this.submitButton.classList.remove('loading');
            if (this.cart && this.cart.classList.contains('is-empty')) this.cart.classList.remove('is-empty');
            if (!this.error) this.submitButton.removeAttribute('aria-disabled');
            this.querySelector('.loading__spinner').classList.add('hidden');

            CartPerformance.measureFromEvent("add:user-action", evt);
          });
      }

      /*
       * Add-on line items (e.g. the Hard Drive dropdown on DVR/NVR pages).
       *
       * The dropdown is associated with the product form via its `form` attribute,
       * so its value arrives in the form data. It is posted as a second, separate
       * /cart/add request after the product itself has been added, which keeps the
       * add-on out of the main post and leaves the recorder's own error handling
       * untouched. Adding the product first means a failed add never leaves an
       * orphaned add-on in the cart.
       */
      static getAddOnItems(formData) {
        /*
         * Each dropdown has a quantity stepper beside it, so the two field lists line
         * up by position. The stepper is the number of add-ons wanted outright — it is
         * not multiplied by how many of the product are being bought.
         */
        return ProductForm.ADD_ON_FIELDS.reduce((items, field) => {
          const variantIds = formData.getAll(field.select);
          const quantities = formData.getAll(field.quantity);

          variantIds.forEach((variantId, index) => {
            if (!variantId) return;

            items.push({
              id: variantId,
              quantity: Math.max(1, parseInt(quantities[index], 10) || 1),
            });
          });

          return items;
        }, []);
      }

      addAddOnItems(addOnItems, formData) {
        // Tag each add-on with the variant it accompanies so the cart can remove
        // them together when the parent line is removed.
        const parentVariantId = formData.get('id');
        const items = addOnItems.map(({ id, quantity }) => ({
          id,
          quantity,
          properties: { [ADD_ON_PARENT_PROPERTY]: parentVariantId },
        }));

        const body = { items };
        const sections = formData.get('sections');

        if (sections) {
          body.sections = sections;
          body.sections_url = formData.get('sections_url');
        }

        return fetch(`${routes.cart_add_url}`, { ...fetchConfig('javascript'), ...{ body: JSON.stringify(body) } })
          .then((response) => response.json())
          .then((response) => {
            if (response.status) {
              this.handleErrorMessage(response.description || response.message);
              return null;
            }
            return response;
          })
          .catch((e) => {
            console.error(e);
            return null;
          });
      }

      handleErrorMessage(errorMessage = false) {
        if (this.hideErrors) return;

        this.errorMessageWrapper =
          this.errorMessageWrapper || this.querySelector('.product-form__error-message-wrapper');
        if (!this.errorMessageWrapper) return;
        this.errorMessage = this.errorMessage || this.errorMessageWrapper.querySelector('.product-form__error-message');

        this.errorMessageWrapper.toggleAttribute('hidden', !errorMessage);

        if (errorMessage) {
          this.errorMessage.textContent = errorMessage;
        }
      }

      toggleSubmitButton(disable = true, text) {
        if (disable) {
          this.submitButton.setAttribute('disabled', 'disabled');
          if (text) this.submitButtonText.textContent = text;
        } else {
          this.submitButton.removeAttribute('disabled');
          this.submitButtonText.textContent = window.variantStrings.addToCart;
        }
      }

      get variantIdInput() {
        return this.form.querySelector('[name=id]');
      }

      static ADD_ON_FIELDS = [{ select: 'hard_drive_variant_id', quantity: 'hard_drive_quantity' }];
    }
  );
}
